import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeMailHtml } from './sanitize';

/**
 * iframe に入れる文書を作る。本文はサニタイズしてから入れ、文書の CSP でスクリプト・フォームの送信先・
 * 外部の読み込みを止める。外部画像は許可されたときだけ img-src に http(s) を足す。
 */
export const buildMailSrcDoc = (sanitizedHtml: string, allowRemoteImages: boolean): string => {
  const imgSrc = allowRemoteImages ? 'data: https: http:' : 'data:';
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'">
<meta name="referrer" content="no-referrer">
<base target="_blank">
<style>
  html,body{margin:0;padding:0;background:#fff;color:#111;}
  body{font-family:system-ui,-apple-system,"Hiragino Sans","Noto Sans JP",sans-serif;font-size:14px;line-height:1.6;word-break:break-word;overflow-wrap:anywhere;}
  img{max-width:100%;height:auto;}
  table{max-width:100%;}
  pre{white-space:pre-wrap;}
  blockquote{margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex;}
  ${allowRemoteImages ? '' : 'img:not([src^="data:"]){display:none;}'}
</style></head><body>${sanitizedHtml}</body></html>`;
};

/**
 * 受信した HTML 本文を表示する（受信の詳細とメーラーの両方で使う）。
 * 3 重に守る: サニタイズ（sanitize.ts）・文書の CSP・スクリプトを許さないサンドボックスの iframe。
 * 外部画像は開封の通知（トラッキング）に使われるため、許可されるまで読み込まない。
 */
export const MailHtmlBody = ({ html, allowRemoteImages }: { html: string; allowRemoteImages: boolean }) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);

  const sanitized = useMemo(() => sanitizeMailHtml(html), [html]);
  const srcDoc = buildMailSrcDoc(sanitized, allowRemoteImages);

  const measure = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc?.documentElement) return;
    setHeight(Math.min(Math.max(doc.documentElement.scrollHeight, 60), 20_000));
  }, []);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    const onLoad = () => {
      measure();
      // 画像の読み込みで高さが変わるため、しばらく測り直す
      const doc = frame.contentDocument;
      doc?.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure));
    };
    frame.addEventListener('load', onLoad);
    return () => frame.removeEventListener('load', onLoad);
  }, [measure, srcDoc]);

  return (
    <iframe
      ref={ref}
      title="メール本文"
      // allow-same-origin は高さを測るためだけに付ける。allow-scripts を付けないのでスクリプトは動かない
      // （同じオリジンでも、スクリプトが動かなければ画面側の DOM や認証情報には触れない）。
      // allow-popups-to-escape-sandbox は、リンクを開いた先のサイトを普通に表示するため。
      // allow-forms は付けないので、フォームは送信できない
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      className="w-full rounded-control border-0 bg-background"
      style={{ height }}
    />
  );
};

/** テキスト本文。「>」で始まる引用は Gmail の「…」と同じく折りたたむ */
export const MailTextBody = ({ text }: { text: string }) => {
  const [showQuote, setShowQuote] = useState(false);
  const { body, quote } = splitQuote(text);
  return (
    <div className="text-body">
      <p className="whitespace-pre-wrap break-words">{body}</p>
      {quote && (
        <>
          <button
            type="button"
            onClick={() => setShowQuote((prev) => !prev)}
            className="mt-2 rounded-control border border-border px-2 text-note text-foreground-muted hover:bg-muted"
            aria-label={showQuote ? '引用を隠す' : '引用を表示'}
            aria-expanded={showQuote}
          >
            •••
          </button>
          {showQuote && <p className="mt-2 whitespace-pre-wrap break-words text-foreground-muted">{quote}</p>}
        </>
      )}
    </div>
  );
};

/** 末尾の引用ブロック（「日時 名前 <アドレス>:」または「On ... wrote:」以降）を分ける */
export const splitQuote = (text: string): { body: string; quote: string | null } => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isHeader =
      /^On .+ wrote:\s*$/.test(line) ||
      /^\d{4}[/年].+[:：]\s*$/.test(line) ||
      /^-{3,}\s*(Original Message|転送されたメッセージ|Forwarded message)/i.test(line);
    const restQuoted = lines.slice(i + 1).filter((l) => l.trim()).every((l) => l.startsWith('>'));
    if ((isHeader && lines.slice(i + 1).some((l) => l.startsWith('>'))) || (line.startsWith('>') && restQuoted)) {
      const body = lines.slice(0, i).join('\n').trimEnd();
      if (!body) return { body: text, quote: null };
      return { body, quote: lines.slice(i).join('\n') };
    }
  }
  return { body: text, quote: null };
};
